import { Pause as PauseIcon, PlayArrow as PlayIcon } from '@mui/icons-material';
import { CircularProgress, useTheme } from '@mui/material';
import { useCallback, useMemo } from 'react';
import {
  StyledIconButton,
  StyledLoadingSpinnerContainer,
} from 'components/PlayerButton/styled';
import { AnalyticsEventLocation } from 'services/analytics/entities';
import useAudioContext from 'services/hooks/wavesurfer';
import { Song } from 'services/store/songs/types';
import useAnalytics from 'services/hooks/analytics';
import { events } from 'services/analytics';

type Props = {
  song: Song | null;
  location: AnalyticsEventLocation;
  isPlayIconEnlarged?: boolean;
};
const AudioPlayerButton = ({ song, location, isPlayIconEnlarged }: Props) => {
  const theme = useTheme();

  const {
    isPlaying,
    isLoading,
    song: playerSong,
    changeSong,
    togglePlayback,
  } = useAudioContext();
  const { track } = useAnalytics();

  const isCurrentSong = useMemo(
    () => song?.id === playerSong?.id,
    [song, playerSong]
  );
  const trackPlaySong = useCallback(() => {
    if (isPlaying || !song) {
      return;
    }

    // @ts-ignore
    const { title, genres } = song;
    const mainGenre = genres?.[0]?.name ?? '';
    track(events.playSong(title, mainGenre, location));
  }, [isPlaying, song, track, location]);

  const handleClick = useCallback(() => {
    trackPlaySong();
    if (isCurrentSong) {
      togglePlayback();
      return;
    }

    changeSong(song);
  }, [song, isCurrentSong, trackPlaySong, togglePlayback, changeSong]);

  if (!song) {
    return null;
  }

  if (isLoading && isCurrentSong) {
    return (
      <StyledLoadingSpinnerContainer isPlayIconEnlarged={isPlayIconEnlarged}>
        <CircularProgress size={theme.spacing(4)} />
      </StyledLoadingSpinnerContainer>
    );
  }

  return (
    <StyledIconButton
      data-testid="player-icon-button"
      isPlayIconEnlarged={isPlayIconEnlarged}
      onClick={handleClick}
    >
      {isPlaying && isCurrentSong ? (
        <PauseIcon
          data-testid="playerPauseButton"
          sx={{
            fontSize: isPlayIconEnlarged ? theme.spacing(5) : theme.spacing(3),
          }}
        />
      ) : (
        <PlayIcon
          data-testid="playerPlayButton"
          sx={{
            fontSize: isPlayIconEnlarged ? theme.spacing(5) : theme.spacing(3),
          }}
        />
      )}
    </StyledIconButton>
  );
};

export default AudioPlayerButton;
